import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getSubscription, setSubscription as persistSubscription } from '../utils/db';
import { canAccess, getLimit, isPlantUnlocked } from '../utils/gates';
import { TRIAL_DURATION_DAYS } from '../services/payment/types';
import { getStoredAuth } from '../services/nativeAppleSignIn';
import { startTrial as startTrialApi, getSubscriptionStatus } from '../services/api';

export const SubscriptionContext = createContext(null);

const DEFAULT_SUBSCRIPTION = {
  tier: 'free',
  status: 'none',
  plan: null,
  trialStartDate: null,
  trialEndDate: null,
  currentPeriodEnd: null,
  stripeCustomerId: null,
  lastVerified: null,
};

const VERIFY_STALE_MS = 60 * 60 * 1000; // 1 hour

/**
 * Resolve the effective tier from raw subscription state.
 * Handles trial expiry and subscription status checks.
 *
 * Paid subscription states ('active', 'past_due') are only trusted
 * if they were verified with the backend within the last 24 hours.
 * This prevents stale IndexedDB data from granting premium access
 * indefinitely (e.g. after a cancelled subscription or on a fresh device).
 */
const VERIFY_TRUST_MS = 24 * 60 * 60 * 1000; // 24 hours

function resolveSubscription(raw) {
  const sub = { ...raw };
  const now = new Date().toISOString();

  // Active trial? (client-managed, no backend verification needed)
  if (sub.status === 'trialing' && sub.trialEndDate) {
    if (now < sub.trialEndDate) {
      sub.tier = 'premium';
      return sub;
    }
    // Trial expired
    sub.status = 'none';
    sub.tier = 'free';
    return sub;
  }

  // Active paid subscription — only trust if recently verified with backend
  if (sub.status === 'active' || sub.status === 'past_due') {
    const lastVerified = sub.lastVerified ? new Date(sub.lastVerified).getTime() : 0;
    const isVerified = (Date.now() - lastVerified) < VERIFY_TRUST_MS;
    if (isVerified) {
      sub.tier = 'premium';
      return sub;
    }
    // Not recently verified — don't trust stale local data
    sub.tier = 'free';
    return sub;
  }

  sub.tier = 'free';
  return sub;
}

export function SubscriptionProvider({ children, paymentAdapter }) {
  const [subscription, setSubState] = useState(DEFAULT_SUBSCRIPTION);
  const [loading, setLoading] = useState(true);

  // Load from IndexedDB on mount
  useEffect(() => {
    let cancelled = false;
    getSubscription().then((raw) => {
      if (cancelled) return;
      // If auth is gone at mount, IndexedDB state can't be trusted (signed-out
      // user must not see a previous account's trial/premium). Reset to default.
      if (!getStoredAuth()?.idToken) {
        setSubState(DEFAULT_SUBSCRIPTION);
        persistSubscription(DEFAULT_SUBSCRIPTION);
      } else {
        setSubState(resolveSubscription(raw));
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  // Sync trial/subscription state from backend whenever the signed-in user
  // changes. The IAP-driven restore() only queries StoreKit on iOS, which
  // doesn't know about server-managed trials — without this, a signed-in user
  // who clears local data would lose their active trial banner.
  useEffect(() => {
    if (loading) return;
    let cancelled = false;
    const syncFromBackend = async () => {
      const auth = getStoredAuth();
      if (!auth?.userId || !auth?.idToken) {
        // Auth gone (sign-out or token expiry) — drop premium/trial state so
        // the trial banner and Subscription panel stop showing stale entitlement.
        if (cancelled) return;
        setSubState((prev) => {
          if (prev.status === 'none' && prev.tier === 'free') return prev;
          persistSubscription(DEFAULT_SUBSCRIPTION);
          return DEFAULT_SUBSCRIPTION;
        });
        return;
      }
      try {
        const remote = await getSubscriptionStatus(auth.userId);
        if (cancelled || !remote) return;
        setSubState((prev) => {
          const updates = { ...prev, lastVerified: new Date().toISOString() };
          if (remote.status === 'trialing' && remote.trialExpiresAt) {
            updates.status = 'trialing';
            updates.trialEndDate = remote.trialExpiresAt;
            if (!prev.trialStartDate) {
              const start = new Date(remote.trialExpiresAt);
              start.setDate(start.getDate() - TRIAL_DURATION_DAYS);
              updates.trialStartDate = start.toISOString();
            }
          } else if (remote.status === 'active' || remote.status === 'past_due') {
            updates.status = remote.status;
            updates.plan = remote.plan;
            updates.currentPeriodEnd = remote.currentPeriodEnd;
            updates.stripeCustomerId = remote.stripeCustomerId;
          }
          const resolved = resolveSubscription(updates);
          persistSubscription(resolved);
          return resolved;
        });
      } catch {
        // backend unavailable — keep cached state
      }
    };
    syncFromBackend();
    const handler = () => syncFromBackend();
    window.addEventListener('storage', handler);
    window.addEventListener('focus', handler);
    window.addEventListener('pomogrow:auth-change', handler);
    return () => {
      cancelled = true;
      window.removeEventListener('storage', handler);
      window.removeEventListener('focus', handler);
      window.removeEventListener('pomogrow:auth-change', handler);
    };
  }, [loading]);

  // Register IAP subscription change callback.
  // Uses functional state update to avoid stale closure over subscription.
  useEffect(() => {
    if (!paymentAdapter || !paymentAdapter.setOnSubscriptionChange) return;
    paymentAdapter.setOnSubscriptionChange((state) => {
      if (state) {
        setSubState((prev) => {
          const resolved = resolveSubscription({ ...prev, ...state, lastVerified: new Date().toISOString() });
          persistSubscription(resolved);
          return resolved;
        });
      }
    });
    return () => {
      if (paymentAdapter.setOnSubscriptionChange) {
        paymentAdapter.setOnSubscriptionChange(null);
      }
    };
  }, [paymentAdapter]);

  // Periodic re-verification with backend (if online & stale)
  useEffect(() => {
    if (loading || !paymentAdapter) return;
    const check = async () => {
      const lastVerified = subscription.lastVerified
        ? new Date(subscription.lastVerified).getTime()
        : 0;
      const stale = Date.now() - lastVerified > VERIFY_STALE_MS;
      if (stale && navigator.onLine) {
        try {
          const remote = await paymentAdapter.restore();
          if (remote) {
            const resolved = resolveSubscription({ ...subscription, ...remote, lastVerified: new Date().toISOString() });
            setSubState(resolved);
            await persistSubscription(resolved);
          }
        } catch {
          // offline or backend unavailable — use cached state
        }
      }
    };
    check();
    const interval = setInterval(check, VERIFY_STALE_MS);
    return () => clearInterval(interval);
  }, [loading, paymentAdapter, subscription.lastVerified]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist subscription changes to IndexedDB
  const updateSubscription = useCallback(async (updates) => {
    const merged = resolveSubscription({ ...subscription, ...updates });
    setSubState(merged);
    await persistSubscription(merged);
    return merged;
  }, [subscription]);

  // Start 7-day free trial — requires a signed-in account so the server
  // can track trial eligibility (one trial per Cognito user, not per
  // device, otherwise clearing local data resets eligibility).
  const startTrial = useCallback(async () => {
    if (!getStoredAuth()?.idToken) {
      const err = new Error('Sign in to start your free trial');
      err.code = 'auth_required';
      throw err;
    }
    let trialEndDate;
    try {
      const remote = await startTrialApi();
      trialEndDate = remote.trialExpiresAt;
    } catch (err) {
      throw new Error(err.message || 'Could not start trial');
    }
    const now = new Date();
    if (!trialEndDate) {
      const fallback = new Date(now);
      fallback.setDate(fallback.getDate() + TRIAL_DURATION_DAYS);
      trialEndDate = fallback.toISOString();
    }
    return updateSubscription({
      status: 'trialing',
      trialStartDate: now.toISOString(),
      trialEndDate,
      lastVerified: now.toISOString(),
    });
  }, [updateSubscription]);

  // Refresh subscription from backend
  const refreshSubscription = useCallback(async () => {
    if (!paymentAdapter) return;
    try {
      const remote = await paymentAdapter.restore();
      if (remote) {
        await updateSubscription({ ...remote, lastVerified: new Date().toISOString() });
      }
    } catch {
      // silent fail
    }
  }, [paymentAdapter, updateSubscription]);

  const contextValue = useMemo(() => ({
    subscription,
    loading,
    canAccess: (feature) => canAccess(subscription, feature),
    getLimit: (feature) => getLimit(subscription, feature),
    isPlantUnlocked: (species) => isPlantUnlocked(subscription, species),
    startTrial,
    refreshSubscription,
    updateSubscription,
    isPremium: subscription.tier === 'premium',
    isTrialing: subscription.status === 'trialing',
    trialDaysRemaining: subscription.trialEndDate
      ? Math.max(0, Math.ceil((new Date(subscription.trialEndDate) - Date.now()) / (1000 * 60 * 60 * 24)))
      : null,
  }), [subscription, loading, startTrial, refreshSubscription, updateSubscription]);

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
}
